// YNX — Frontend diagnostics reporter (fail-silent BY DESIGN).
// ─────────────────────────────────────────────────────────────
// IMPORTANT: this module's catch blocks intentionally never `throw`
// or `console.error`. Reason: the reporter is wired to `window.onerror`
// and `unhandledrejection`. If it surfaced its OWN errors, we would
// trigger infinite recursion (each report → emits an error → reported).
// Failures are logged at `console.debug` ONLY, so production dev tools
// still see them while the host application stays unaffected.
const API = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API}/api/biz/diagnostics/error`;

const _seen = new Set();        // dedup last 8s
const SEEN_TTL_MS = 8000;
let _enabled = false;

const _hash = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0;
  }
  return h.toString(36);
};

const _post = (payload) => {
  // Fail-silent: serializing or sending must never crash the host app.
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      mode: 'cors',
    }).catch((err) => {
      // No-recursion: NEVER throw or call console.error from inside the
      // diagnostics pipeline. Debug-only.
      console.debug('YNX diagnostics: send failed (silently)', err);
    });
  } catch (err) {
    console.debug('YNX diagnostics: payload serialize failed', err);
  }
};

const _readStorage = (key) => {
  try { return localStorage.getItem(key) || ''; }
  catch (err) {
    console.debug(`YNX diagnostics: localStorage[${key}] blocked`, err);
    return '';
  }
};

/** Report a single client-side error. Returns true if sent. */
export const reportError = (info = {}) => {
  if (!_enabled) return false;
  try {
    const key = _hash(`${info.error_type || 'unknown'}|${(info.message || '').slice(0, 80)}|${info.url || ''}`);
    if (_seen.has(key)) return false;
    _seen.add(key);
    setTimeout(() => _seen.delete(key), SEEN_TTL_MS);

    _post({
      source:       info.source || 'frontend',
      severity:     info.severity || 'error',
      error_type:   (info.error_type || 'unknown').slice(0, 80),
      message:      String(info.message || '').slice(0, 2000),
      stack:        String(info.stack || '').slice(0, 4000),
      url:          String(window.location?.href || '').slice(0, 500),
      user_agent:   String(navigator.userAgent || '').slice(0, 400),
      visitor_id:   _readStorage('ynx_visitor_id'),
      workspace_id: _readStorage('ynx_workspace_id'),
      context:      info.context || {},
    });
    return true;
  } catch (err) {
    console.debug('YNX diagnostics: reportError failed', err);
    return false;
  }
};

/** Wire window.onerror + unhandledrejection. Safe to call multiple times. */
export const initDiagnostics = () => {
  if (_enabled) return;
  _enabled = true;
  try {
    window.addEventListener('error', (e) => {
      // Ignore Google Translate / Grammarly DOM noise — these are harmless.
      const msg = String(e?.message || '');
      if (msg.includes('ResizeObserver loop')) return;
      if (msg.includes('Script error.') && !e?.filename) return;
      reportError({
        error_type: e?.error?.name || 'WindowError',
        message:    msg || 'window.error',
        stack:      e?.error?.stack || `${e?.filename}:${e?.lineno}:${e?.colno}`,
      });
    }, true);

    window.addEventListener('unhandledrejection', (e) => {
      const reason = e?.reason;
      reportError({
        error_type: 'UnhandledRejection',
        message:    String(reason?.message || reason || 'unhandledrejection'),
        stack:      reason?.stack || '',
      });
    });
  } catch (err) {
    console.debug('YNX diagnostics: init listeners failed', err);
  }
};

export const isDiagnosticsEnabled = () => _enabled;
